import type { FilterField } from '@orbind/ui'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { api } from '../../lib/api.ts'
import { useBootstrap } from '../../lib/catalog.ts'
import { useT } from '../../lib/i18n.tsx'
import { useCustomFields } from '../admin/custom-field-data.ts'
import { rotuloCampo } from './campos-shared.tsx'
import {
  CAMPOS_DE_FILTRO,
  type EtapaDoFiltro,
  fazerLoadValueOptions,
  fazerRowValues,
  tipoDoCampoComplementar,
  valorDaEtapa,
} from './filter-fields.ts'
export { passaNaSelecao } from './selecao-do-filtro.ts'

/**
 * O filtro dos Itens de Trabalho fora da lista: o MESMO da lista de Itens —
 * Todos / Meus / Atrasados, os filtros rápidos salvos e as condições por campo
 * (SPEC-20261008-2157-filtro-no-seletor-de-itens, pedido do Cristian: "pode
 * reaproveitar"). Mora aqui para a lista e o seletor de itens decidirem igual.
 */

/** Tudo o que o componente de filtro precisa: opções, campos e leitura da linha. */
export function useFiltroDeItens(etapas: EtapaDoFiltro[] = []) {
  const t = useT()
  const bootstrap = useBootstrap()
  const etiquetas = useQuery({
    queryKey: ['labels-all'],
    queryFn: async () => (await api['work-items'].labels.all.$get()).json(),
  })
  const quadros = useQuery({
    queryKey: ['kanbans'],
    queryFn: async () => (await api.kanbans.$get()).json(),
  })
  const portfolio = useQuery({
    queryKey: ['portfolio', 'tree'],
    queryFn: async () => (await api.portfolio.tree.$get()).json(),
  })
  const complementares = useCustomFields('work_item')

  return useMemo(() => {
    const rotulo = (row: { code: string; nameTranslationKey?: string | null }) => {
      const key = row.nameTranslationKey ?? `x.${row.code}`
      const label = t(key)
      return label === key ? row.code : label
    }
    const nos = new Map<string, string>()
    const andar = (lista: readonly { id: string; name: string; children?: unknown }[]) => {
      for (const no of lista) {
        nos.set(no.id, no.name)
        andar((no.children ?? []) as never)
      }
    }
    andar(((portfolio.data as { nodes?: unknown[] } | undefined)?.nodes ?? []) as never)

    const filtraveis = (complementares.data?.fields ?? []).filter((f) => f.defaultVisible)
    const campos: readonly FilterField[] = [
      ...CAMPOS_DE_FILTRO,
      ...filtraveis.map((f) => ({
        key: `cf:${f.key}`,
        label: rotuloCampo(f.labels, f.key),
        type: tipoDoCampoComplementar(f.type),
        placeholder: 'Valor…',
      })),
    ]
    const base = fazerLoadValueOptions(bootstrap.data, t, etiquetas.data?.labels, quadros.data?.boards, {
      etapas,
      portfolios: [...nos.values()],
    })
    const loadValueOptions = (fieldKey: string, query: string) => {
      if (!fieldKey.startsWith('cf:')) return base(fieldKey, query)
      const campo = filtraveis.find((c) => `cf:${c.key}` === fieldKey)
      if (!campo) return []
      const casa = (r: string) => r.toLowerCase().includes(query.trim().toLowerCase())
      if (campo.type === 'boolean') return [{ value: 'true', label: 'Sim' }, { value: 'false', label: 'Não' }].filter((o) => casa(o.label))
      return ((campo.config as { options?: { value: string; label: string }[] }).options ?? []).filter((o) => casa(o.label))
    }
    return {
      statusOptions: (bootstrap.data?.workItemStatuses ?? []).map((s) => ({ value: s.id, label: rotulo(s) })),
      typeOptions: (bootstrap.data?.workItemTypes ?? []).filter((x) => x.isActive).map((x) => ({ value: x.id, label: rotulo(x) })),
      priorityOptions: (bootstrap.data?.priorities ?? []).map((p) => ({ value: p.id, label: rotulo(p) })),
      memberOptions: (bootstrap.data?.members ?? []).map((m) => ({ value: m.id, label: m.name })),
      loadValueOptions,
      campos,
      rowValues: fazerRowValues(bootstrap.data, t, (id) => nos.get(id)),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrap.data, etiquetas.data, quadros.data, portfolio.data, complementares.data, t, etapas.map(valorDaEtapa).join('|')])
}
